import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { User } from '../types';
import { callApproveRestaurantPurge } from '../services/restaurantLifecycleService';

interface AdminAlert {
  id: string;
  type?: string;
  restaurantId?: string;
  restaurantName?: string;
  message?: string;
  status?: string;
  requestedByName?: string;
  requestedByEmail?: string;
}

interface AdminAlertsPopupProps {
  currentUser: User;
}

/** Firebase adminAlerts → 딜러/총판/Admin 팝업 (완전삭제 승인 요청) */
export default function AdminAlertsPopup({ currentUser }: AdminAlertsPopupProps) {
  const [alerts, setAlerts] = useState<AdminAlert[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser?.uid) return;

    let unsub: Unsubscribe | null = null;
    let unsubFallback: Unsubscribe | null = null;
    let cancelled = false;

    const applySnap = (snap: {
      forEach: (fn: (d: { id: string; data: () => Record<string, unknown> }) => void) => void;
    }) => {
      try {
        const list: AdminAlert[] = [];
        snap.forEach((d) => {
          try {
            list.push({ id: d.id, ...d.data() } as AdminAlert);
          } catch (e) {
            console.warn('[AdminAlertsPopup] skip alert doc', d.id, e);
          }
        });
        if (!cancelled) {
          setAlerts(
            list.filter(
              (a) =>
                a.type === 'purge_approval_request' &&
                a.status === 'pending' &&
                !!a.restaurantId
            )
          );
        }
      } catch (e) {
        console.warn('[AdminAlertsPopup] applySnap failed', e);
        if (!cancelled) setAlerts([]);
      }
    };

    const onPermError = (err: unknown) => {
      console.warn('[AdminAlertsPopup] listener error (ignored)', err);
      if (!cancelled) setAlerts([]);
    };

    try {
      const q = query(
        collection(db, 'adminAlerts'),
        where('recipientUid', '==', currentUser.uid),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      unsub = onSnapshot(
        q,
        (snap) => applySnap(snap),
        () => {
          // Composite index may be missing — fallback without orderBy
          try {
            const q2 = query(
              collection(db, 'adminAlerts'),
              where('recipientUid', '==', currentUser.uid),
              where('status', '==', 'pending'),
              limit(20)
            );
            unsubFallback = onSnapshot(q2, (snap) => applySnap(snap), onPermError);
          } catch (e) {
            onPermError(e);
          }
        }
      );
    } catch (e) {
      onPermError(e);
    }

    return () => {
      cancelled = true;
      unsub?.();
      unsubFallback?.();
    };
  }, [currentUser?.uid]);

  if (alerts.length === 0) return null;

  const alert = alerts[0];
  if (!alert?.restaurantId) return null;

  const handle = async (approve: boolean) => {
    if (!alert.restaurantId) return;
    setBusyId(alert.id);
    setError('');
    try {
      const result = await callApproveRestaurantPurge({
        restaurantId: alert.restaurantId,
        alertId: alert.id,
        approve,
      });
      if (!result.success) {
        setError(result.message || 'Request failed');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.55)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          maxWidth: 480,
          width: '100%',
          padding: 24,
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          border: '1px solid #fecaca',
        }}
      >
        <h2 style={{ margin: '0 0 8px', color: '#991b1b', fontSize: 18 }}>
          Deletion Approval Request
        </h2>
        <p style={{ margin: '0 0 12px', color: '#334155', fontSize: 14, lineHeight: 1.5 }}>
          {alert.message ||
            `Approve permanent delete for "${alert.restaurantName || 'restaurant'}"?`}
        </p>
        {(alert.requestedByName || alert.requestedByEmail) && (
          <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b' }}>
            Requested by: {alert.requestedByName || alert.requestedByEmail}
          </p>
        )}
        {error && (
          <p style={{ margin: '0 0 12px', color: '#dc2626', fontSize: 13 }}>{error}</p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            disabled={!!busyId}
            onClick={() => handle(false)}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              fontWeight: 600,
              cursor: busyId ? 'wait' : 'pointer',
            }}
          >
            Reject
          </button>
          <button
            type="button"
            disabled={!!busyId}
            onClick={() => {
              if (
                !window.confirm(
                  `Permanently delete "${alert.restaurantName}"?\n\nThis erases ALL remaining data including tax archives and cannot be undone.`
                )
              ) {
                return;
              }
              handle(true);
            }}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              cursor: busyId ? 'wait' : 'pointer',
            }}
          >
            {busyId ? 'Working…' : 'Approve Delete'}
          </button>
        </div>
        {alerts.length > 1 && (
          <p style={{ margin: '12px 0 0', fontSize: 12, color: '#94a3b8' }}>
            +{alerts.length - 1} more pending alert(s)
          </p>
        )}
      </div>
    </div>
  );
}
